import { useLayoutEffect } from 'react';

/**
 * Seo — per-page <head> manager (Phase 126, SEO audit fix).
 *
 * WHY THIS IS IMPERATIVE (and no longer react-helmet-async)
 * ---------------------------------------------------------
 * We run React 19. react-helmet-async v3 detects React 19 and switches to
 * its "React19Dispatcher", which simply RENDERS <title>/<meta>/<link>
 * elements and lets React hoist them into <head>. React hoists — it does
 * not replace — so every page ended up with TWO <title> tags and TWO
 * <meta name="description"> tags: the static defaults from
 * /public/index.html plus the page's own. Crawlers read the FIRST one,
 * which is why a site-wide SEO audit reported "56 pages have identical
 * meta descriptions" and duplicate titles.
 *
 * This component instead UPSERTS: it finds the existing tag in <head>
 * (the static default from index.html, or one we created earlier) and
 * updates it in place. Exactly one <title>, one description, one
 * canonical, one og:* / twitter:* set — always. On unmount (route change
 * to a page that doesn't render <Seo>) it restores the static defaults so
 * app-internal pages still have a sensible title.
 *
 * Props are unchanged from the previous Helmet-based version, so every
 * existing call site keeps working:
 *   title, description, canonical, image, imageAlt, type, publishedTime,
 *   modifiedTime, keywords, noIndex, jsonLd (object or array of objects)
 *
 * Multiple <Seo> instances on one page are NOT supported by design (last
 * one to run wins, and unmount order could restore stale values). Render
 * exactly one per route.
 */
const DEFAULT_SITE = 'https://splashpad.pro';
const DEFAULT_IMAGE = `${DEFAULT_SITE}/splashpad-logo.png`;
const MANAGED_ATTR = 'data-seo-managed';
const JSONLD_ATTR = 'data-seo-jsonld';

// Snapshot of the static defaults from index.html, captured once on first
// use so we can restore them when a page-level <Seo> unmounts.
let _defaults = null;

function head() {
    return typeof document !== 'undefined' ? document.head : null;
}

function findMeta(selector) {
    const h = head();
    if (!h) return null;
    // Prefer a tag we already manage; otherwise the static one.
    return h.querySelector(`${selector}[${MANAGED_ATTR}]`) || h.querySelector(selector);
}

/** Upsert a <meta> identified by `attr="key"`. Removes duplicates. */
function setMeta(attr, key, content) {
    const h = head();
    if (!h) return;
    const selector = `meta[${attr}="${key}"]`;
    const all = Array.from(h.querySelectorAll(selector));
    if (content === undefined || content === null || content === '') {
        // Explicitly empty → remove every instance (used for noindex toggling).
        all.forEach((el) => el.remove());
        return;
    }
    let el = all[0] || null;
    // Collapse duplicates (e.g. left behind by the old Helmet path).
    all.slice(1).forEach((dupe) => dupe.remove());
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        h.appendChild(el);
    }
    if (el.getAttribute('content') !== content) el.setAttribute('content', content);
    el.setAttribute(MANAGED_ATTR, 'true');
}

function setTitle(title) {
    const h = head();
    if (!h) return;
    const titles = Array.from(h.querySelectorAll('title'));
    // Keep exactly one <title>.
    titles.slice(1).forEach((t) => t.remove());
    if (title === undefined || title === null) return;
    if (document.title !== title) document.title = title;
}

function setCanonical(href) {
    const h = head();
    if (!h) return;
    const links = Array.from(h.querySelectorAll('link[rel="canonical"]'));
    if (!href) {
        links.forEach((l) => l.remove());
        return;
    }
    let el = links[0] || null;
    links.slice(1).forEach((dupe) => dupe.remove());
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        h.appendChild(el);
    }
    if (el.getAttribute('href') !== href) el.setAttribute('href', href);
    el.setAttribute(MANAGED_ATTR, 'true');
}

function setJsonLd(blocks) {
    const h = head();
    if (!h) return;
    // Remove only the page-level blocks we own — the site-wide
    // Organization graph in index.html is untouched.
    Array.from(h.querySelectorAll(`script[${JSONLD_ATTR}]`)).forEach((s) => s.remove());
    (blocks || []).forEach((block) => {
        if (!block) return;
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute(JSONLD_ATTR, 'true');
        s.text = JSON.stringify(block);
        h.appendChild(s);
    });
}

function captureDefaults() {
    if (_defaults) return _defaults;
    const read = (sel) => findMeta(sel)?.getAttribute('content') ?? null;
    _defaults = {
        title: typeof document !== 'undefined' ? document.title : '',
        description: read('meta[name="description"]'),
        keywords: read('meta[name="keywords"]'),
        robots: read('meta[name="robots"]'),
        ogType: read('meta[property="og:type"]'),
        ogTitle: read('meta[property="og:title"]'),
        ogDescription: read('meta[property="og:description"]'),
        ogUrl: read('meta[property="og:url"]'),
        ogImage: read('meta[property="og:image"]'),
        ogImageAlt: read('meta[property="og:image:alt"]'),
        twCard: read('meta[name="twitter:card"]'),
        twTitle: read('meta[name="twitter:title"]'),
        twDescription: read('meta[name="twitter:description"]'),
        twImage: read('meta[name="twitter:image"]'),
        twImageAlt: read('meta[name="twitter:image:alt"]'),
    };
    return _defaults;
}

function restoreDefaults() {
    const d = _defaults;
    if (!d) return;
    setTitle(d.title);
    setMeta('name', 'description', d.description);
    setMeta('name', 'keywords', d.keywords);
    setMeta('name', 'robots', d.robots);
    setCanonical(null); // index.html deliberately ships no canonical
    setMeta('property', 'og:type', d.ogType);
    setMeta('property', 'og:title', d.ogTitle);
    setMeta('property', 'og:description', d.ogDescription);
    setMeta('property', 'og:url', d.ogUrl);
    setMeta('property', 'og:image', d.ogImage);
    setMeta('property', 'og:image:alt', d.ogImageAlt);
    setMeta('property', 'article:published_time', null);
    setMeta('property', 'article:modified_time', null);
    setMeta('name', 'twitter:card', d.twCard);
    setMeta('name', 'twitter:title', d.twTitle);
    setMeta('name', 'twitter:description', d.twDescription);
    setMeta('name', 'twitter:image', d.twImage);
    setMeta('name', 'twitter:image:alt', d.twImageAlt);
    setJsonLd([]);
}

/** Never let a base64 data: URI leak into share/schema image fields —
 *  it is an invalid URL for schema.org and balloons the HTML. */
function safeImageUrl(url) {
    if (!url || typeof url !== 'string') return DEFAULT_IMAGE;
    if (/^data:/i.test(url.trim())) return DEFAULT_IMAGE;
    return url;
}

/**
 * Apply a full set of head tags. Exported so non-React code (or tests)
 * can drive it; <Seo> is a thin hook wrapper around this.
 */
export function applySeo({
    title,
    description,
    canonical,
    image,
    imageAlt,
    type = 'website',
    publishedTime,
    modifiedTime,
    keywords,
    noIndex = false,
    jsonLd,
} = {}) {
    if (!head()) return;
    const defaults = captureDefaults();

    const url = (() => {
        if (canonical) return canonical;
        try {
            const u = new URL(window.location.href);
            return `${u.origin}${u.pathname}`;
        } catch {
            return DEFAULT_SITE;
        }
    })();

    const resolvedTitle = title || defaults.title;
    const resolvedDescription = description || defaults.description || '';
    const imageUrl = safeImageUrl(image);
    const computedAlt = imageAlt || resolvedTitle || 'SplashPad';

    setTitle(resolvedTitle);
    setMeta('name', 'description', resolvedDescription);
    setMeta('name', 'keywords', keywords || defaults.keywords);
    setMeta('name', 'robots', noIndex ? 'noindex, nofollow' : (defaults.robots || 'index, follow'));
    setCanonical(noIndex ? null : url);

    // Open Graph
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:title', resolvedTitle);
    setMeta('property', 'og:description', resolvedDescription);
    setMeta('property', 'og:image', imageUrl);
    setMeta('property', 'og:image:alt', computedAlt);
    setMeta('property', 'article:published_time', publishedTime || null);
    setMeta('property', 'article:modified_time', modifiedTime || null);

    // Twitter / X
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', resolvedTitle);
    setMeta('name', 'twitter:description', resolvedDescription);
    setMeta('name', 'twitter:image', imageUrl);
    setMeta('name', 'twitter:image:alt', computedAlt);

    // Page-level JSON-LD (object or array)
    const blocks = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    setJsonLd(blocks);
}

export default function Seo(props) {
    const {
        title, description, canonical, image, imageAlt, type, publishedTime,
        modifiedTime, keywords, noIndex, jsonLd,
    } = props;
    // Serialize JSON-LD so the effect re-runs only when its content changes,
    // not on every parent render that hands us a fresh object identity.
    const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

    // useLayoutEffect: mutate <head> before paint so the tab title never
    // flashes the default, and so bot pre-renderers that snapshot after
    // "network idle" always see the final tags.
    useLayoutEffect(() => {
        applySeo({
            title, description, canonical, image, imageAlt, type, publishedTime,
            modifiedTime, keywords, noIndex, jsonLd,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, description, canonical, image, imageAlt, type, publishedTime, modifiedTime, keywords, noIndex, jsonLdKey]);

    // Restore the static defaults when this page's <Seo> unmounts.
    useLayoutEffect(() => () => restoreDefaults(), []);

    return null;
}
